/* eslint-disable no-console */
import {CrownFilled, InfoCircleOutlined} from '@ant-design/icons';
import {Col, Flex, Form, Input, Select} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import moment from 'moment';
import React, {useContext, useEffect, useMemo, useState} from 'react';

import {ReactComponent as YearCalender} from '../../assets/yearCalender.svg';
import {API_ENDPOINT_JSON} from '../../config';
import {CommonErrors, TF} from '../../constants/Common';
import FilteredUnit from '../../constants/FilteredUnit';
import SelectedCountryContext from '../../pages/configurator/orgFacility/Constants/SelectedCountryContext';
import {sortYearOptions} from '../../pages/orgManager/Utils/InfraStructureProfile/constant/sorYear';
import NumberInput from '../../shared/NumberInput';
import yearFilterArray from '../../shared/YearFilterArray';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import useUserStore from '../../store/userStore/userStore';
import {hasUserRole, UserRole} from '../../utils/commonFunctions';
import {eachWordCap} from '../../utils/FormUtils/FirstCap';
import RegEx from '../../utils/FormUtils/RegEx';
import {shouldBlockFreeOrReadOnly} from '../../utils/SubscriptionHelper';
import PhoneNumberInput from '../CustomInputs/PhoneNumberInput/PhoneNumberInput';
import MomentDatePicker from '../DatePicker/MomentDatePicker';
import {MYConfig, MYData} from '../MonthlyYearly/Interface/common';
import MonthlyYearlyDrawerForm from '../MonthlyYearly/MonthlyYearlyDrawerForm';
import {yesNo} from '../Table/Constants/Common';

import {
  documentUrlKeyLabel,
  facilityKeyLabel,
  yearKeyLabel
} from './Constants/IP/commonKeyLabel';
import {
  VehicleKey,
  VehicleLabel,
  VehicleTooltip
} from './Constants/IP/VehicleKeyLabel';
import {unitType, unitTypes} from './interface/unitChange';
import AttachedDocumentUpload from './Utils/AttachedDocumentUpload';
import {getUnits} from './Utils/changeUnit';
import {filterSelectOptions} from './Utils/commonFunctions';

const VehicleForm: React.FC<{
  currentStep?: number;
  defaultDisplayStep?: boolean;
}> = ({currentStep, defaultDisplayStep = false}) => {
  const isCompanyUserOrAdmin =
    hasUserRole(UserRole.Company_Admin) || hasUserRole(UserRole.Company_user);
  const vehicleType = useJSONStore.getState().vehicleType;
  const userDetails = useUserStore.getState().userDetails;
  const vehicleFuelUsed = useJSONStore.getState().vehicleFuelUsed;
  const {getFieldValue, setFields} = useFormInstance();
  const facilityDetails = useCompanyStore.getState().facilityDetails ?? [];
  const [facilityOpt, setFacilityOpt] = useState<any[]>([]);
  const [yearOptions, setYearOptions] = useState<any[]>([]);

  const {efdbData, facilityId, selectedYear, selectedCountry} = useContext(
    SelectedCountryContext
  );
  const [facilityEfdb, setFacilityEfdb] = useState<any>();

  const [formSelectedCountry, setFormSelectedCountry] = useState<string>('');

  const [fuelState, setFuelState] = useState<unitType>(unitTypes.WEIGHT);

  useEffect(() => {
    setFacilityOpt(
      facilityDetails?.[
        getFieldValue(yearKeyLabel.key) ?? selectedYear ?? ''
      ] ?? []
    );
    setFacilityEfdb(
      efdbData?.[getFieldValue(yearKeyLabel.key) ?? selectedYear ?? '']?.find(
        (i: any) => {
          return (
            i.facilityId ===
            (getFieldValue(facilityKeyLabel.key) ?? facilityId ?? '')
          );
        }
      )
    );
  }, []);

  useEffect(() => {
    setFuelState(
      vehicleFuelUsed?.find((item) => {
        return (
          item?.value === getFieldValue(VehicleKey.FUEL_USED) &&
          item?.dbName === facilityEfdb?.scope1
        );
      })?.state ?? ''
    );
  }, [facilityEfdb]);

  useEffect(() => {
    setYearOptions(
      yearFilterArray(
        facilityDetails,
        !isCompanyUserOrAdmin ? userDetails?.facility : undefined
      )
    );
  }, []);

  useEffect(() => {
    setFormSelectedCountry(
      facilityOpt?.find((item: any) => {
        return (
          item?._id === (getFieldValue(facilityKeyLabel.key) ?? facilityId)
        );
      })?.country
    );
  }, [facilityOpt]);

  const monYear: MYConfig[] = useMemo(() => {
    return [
      {
        mainTitle: {
          key: VehicleKey.QUANTITY_TF,
          title: VehicleLabel.QUANTITY_TF,
          tooltip: VehicleTooltip.QUANTITY
        },
        unit: getUnits(
          fuelState,
          FilteredUnit(formSelectedCountry ?? selectedCountry)
        )
      },
      {
        mainTitle: {
          key: VehicleKey.DRIVEN_TF,
          title: VehicleLabel.DRIVEN_TF,
          tooltip: VehicleTooltip.DRIVEN
        },
        unit: FilteredUnit(formSelectedCountry ?? selectedCountry).distance
      },
      {
        mainTitle: {
          key: VehicleKey.FUEL_COST_TF,
          title: VehicleLabel.FUEL_COST_TF,
          tooltip: VehicleTooltip.FUEL_COST
        },
        unit: FilteredUnit(formSelectedCountry ?? selectedCountry).currency
      },
      {
        mainTitle: {
          key: VehicleKey.MAINTENANCE_COST_TF,
          title: VehicleLabel.MAINTENANCE_COST_TF,
          tooltip: VehicleTooltip.MAINTENANCE_COST
        },
        unit: FilteredUnit(formSelectedCountry ?? selectedCountry).currency
      }
    ];
  }, [formSelectedCountry, selectedCountry, fuelState]);

  return (
    <>
      {(currentStep === 0 || defaultDisplayStep) && (
        <>
          <Col className="gutter-row">
            <Form.Item
              name={yearKeyLabel.key}
              label={yearKeyLabel.label}
              labelCol={{span: 24}}
              initialValue={selectedYear}
              hidden={defaultDisplayStep}
            >
              <Select
                placeholder={yearKeyLabel.label}
                options={sortYearOptions(yearOptions)}
                suffixIcon={<YearCalender />}
                disabled={getFieldValue('_id') !== undefined}
                onChange={(e) => {
                  setFacilityOpt(facilityDetails?.[e] ?? []);
                  setFacilityEfdb(() => {
                    return efdbData?.[e]?.find((i: any) => {
                      return (
                        i?.facilityId === getFieldValue(facilityKeyLabel.key)
                      );
                    });
                  });
                  if (getFieldValue(facilityKeyLabel.key)) {
                    setFields([
                      {
                        name: facilityKeyLabel.key,
                        value: undefined,
                        errors: ['Please select facility again']
                      }
                    ]);
                  }
                }}
              />
            </Form.Item>
          </Col>
          {isCompanyUserOrAdmin && (
            <>
              <Col className="gutter-row">
                <Form.Item
                  name={facilityKeyLabel.key}
                  label={facilityKeyLabel.label}
                  labelCol={{span: 24}}
                  hidden={defaultDisplayStep}
                  rules={[
                    {
                      required: true,
                      message: CommonErrors.mandatory(facilityKeyLabel.label)
                    }
                  ]}
                  initialValue={facilityId}
                >
                  <Select
                    placeholder={facilityKeyLabel.label}
                    disabled={getFieldValue('_id') !== undefined}
                    options={
                      facilityOpt?.map((item: any) => {
                        return {label: item.name, value: item._id};
                      }) ?? []
                    }
                    onChange={(e) => {
                      setFacilityEfdb(() => {
                        return efdbData?.[
                          getFieldValue(yearKeyLabel.key)
                        ]?.find((i: any) => {
                          return i?.facilityId === e;
                        });
                      });
                      if (getFieldValue(VehicleKey.FUEL_USED)) {
                        setFields([
                          {
                            name: VehicleKey.FUEL_USED,
                            errors: [CommonErrors.selectAgain],
                            value: undefined
                          }
                        ]);
                      }
                      setFormSelectedCountry(
                        facilityDetails?.[
                          getFieldValue(yearKeyLabel.key)
                        ]?.find((item: any) => {
                          return item?._id === e;
                        })?.country
                      );
                    }}
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </>
      )}
      {(currentStep === 1 || defaultDisplayStep) && (
        <>
          <Col className="gutter-row">
            <Form.Item
              name={VehicleKey.VEHICLE_TYPE}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(VehicleLabel.VEHICLE_TYPE)
                }
              ]}
              label={VehicleLabel.VEHICLE_TYPE}
              labelCol={{span: 24}}
              validateFirst
              hasFeedback
            >
              <Select
                showSearch
                filterOption={filterSelectOptions}
                className="select"
                placeholder={`Choose ${VehicleLabel.VEHICLE_TYPE}`}
                options={vehicleType}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name={VehicleKey.NO_UNITS}
              label={VehicleLabel.NO_UNITS}
              rules={[
                {
                  pattern: RegEx.nonDecimal,
                  message: CommonErrors.nonDecimalNonNegative
                }
              ]}
              initialValue={getFieldValue(VehicleKey.NO_UNITS) ?? null}
              labelCol={{span: 24}}
              validateFirst
              hasFeedback
            >
              <NumberInput placeholder={`Enter ${VehicleLabel.NO_UNITS}`} />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name={VehicleKey.FUEL_USED}
              label={VehicleLabel.FUEL_USED}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(VehicleLabel.FUEL_USED)
                }
              ]}
              labelCol={{span: 24}}
              validateFirst
              hasFeedback
              tooltip={{
                icon: <InfoCircleOutlined />,
                title: VehicleTooltip.FUEL_USED
              }}
            >
              <Select
                showSearch
                filterOption={filterSelectOptions}
                className="select"
                placeholder={`Choose ${VehicleLabel.FUEL_USED}`}
                options={vehicleFuelUsed?.filter((item) => {
                  return item?.dbName === facilityEfdb?.scope1;
                })}
                onChange={(e, option) => {
                  setFuelState(option?.state);
                }}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name={VehicleKey.IS_LEASED}
              label={VehicleLabel.IS_LEASED}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(VehicleLabel.IS_LEASED)
                }
              ]}
              labelCol={{span: 24}}
              validateFirst
              hasFeedback
            >
              <Select
                className="select"
                placeholder={`Choose ${VehicleLabel.IS_LEASED}`}
                options={yesNo.map((option) => {
                  return {
                    ...option,
                    label: (
                      <Flex align="center" justify="space-between">
                        <div>{option.label}</div>
                        {shouldBlockFreeOrReadOnly() &&
                          option.label === 'Yes' && (
                            <CrownFilled
                              style={{
                                color: '#FFD700',
                                paddingRight: 10,
                                paddingLeft: 10
                              }}
                              title="Upgrade Plan"
                            />
                          )}
                      </Flex>
                    ),
                    disabled:
                      shouldBlockFreeOrReadOnly() && option.label === 'Yes'
                  };
                })}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item dependencies={[VehicleKey.IS_LEASED]} noStyle>
              {({getFieldValue}) => {
                return (
                  getFieldValue(VehicleKey.IS_LEASED) && (
                    <Form.Item
                      name={VehicleKey.LESSOR_NAME}
                      rules={[
                        // {
                        //   required: true,
                        //   message: CommonErrors.mandatory(
                        //     VehicleLabel.LESSOR_NAME
                        //   )
                        // },
                        {
                          max: 100,
                          message: `${VehicleLabel.LESSOR_NAME} should be less than 100 characters`
                        },
                        {
                          min: 3,
                          message: `${VehicleLabel.LESSOR_NAME} should be more than 2 characters`
                        },
                        {
                          pattern: RegEx.fullName,
                          message: CommonErrors.noSpecialChar
                        },
                        {
                          whitespace: true,
                          message: CommonErrors.mandatory(
                            VehicleLabel.LESSOR_NAME
                          )
                        }
                      ]}
                      label={VehicleLabel.LESSOR_NAME}
                      labelCol={{span: 24}}
                      initialValue={''}
                      validateFirst
                      hasFeedback
                    >
                      <Input
                        className="input"
                        placeholder={`Enter ${VehicleLabel.LESSOR_NAME}`}
                        onInput={(e) => {
                          const target = e.target as HTMLInputElement;
                          target.value = eachWordCap(target.value);
                        }}
                      />
                    </Form.Item>
                  )
                );
              }}
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item dependencies={[VehicleKey.IS_LEASED]} noStyle>
              {({getFieldValue}) => {
                return (
                  getFieldValue(VehicleKey.IS_LEASED) && (
                    <Form.Item
                      name={VehicleKey.MOBILE_NO}
                      rules={[
                        // {
                        //   required: true,
                        //   message: CommonErrors.mandatory(
                        //     VehicleLabel.MOBILE_NO
                        //   )
                        // },
                        {
                          // eslint-disable-next-line @typescript-eslint/promise-function-async
                          validator(_, value) {
                            const digitsOnly = (value ?? '')
                              .toString()
                              .replace(/\D/g, '');
                            if (!value || digitsOnly.length <= 3) {
                              return Promise.resolve();
                            }
                            if (RegEx.mobile.test(value)) {
                              return Promise.resolve();
                            } else {
                              return Promise.reject(
                                new Error(
                                  CommonErrors.validField(
                                    VehicleLabel.MOBILE_NO
                                  )
                                )
                              );
                            }
                          }
                        }
                      ]}
                      label={VehicleLabel.MOBILE_NO}
                      labelCol={{span: 24}}
                      initialValue={''}
                      validateFirst
                      hasFeedback
                    >
                      <PhoneNumberInput />
                    </Form.Item>
                  )
                );
              }}
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item dependencies={[VehicleKey.IS_LEASED]} noStyle>
              {({getFieldValue}) => {
                return (
                  getFieldValue(VehicleKey.IS_LEASED) && (
                    <Form.Item
                      name={VehicleKey.RENTED_FROM}
                      label={VehicleLabel.RENTED_FROM}
                      rules={[
                        {
                          required: true,
                          message: CommonErrors.mandatory(
                            VehicleLabel.RENTED_FROM
                          )
                        }
                      ]}
                      validateFirst={true}
                      hasFeedback
                      labelCol={{span: 24}}
                    >
                      <MomentDatePicker
                        disabledDate={(current) => {
                          return (
                            moment() <= current || moment('2000/04') > current
                          );
                        }}
                        inputReadOnly
                        format="DD-MM-YYYY"
                        style={{width: '100%'}}
                        showNow={false}
                        placeholder={`Choose ${VehicleLabel.RENTED_FROM}`}
                        onChange={() => {
                          if (getFieldValue(VehicleKey.RENTED_TO)) {
                            setFields([
                              {
                                name: VehicleKey.RENTED_TO,
                                errors: [CommonErrors.selectAgain],
                                value: undefined
                              }
                            ]);
                          }
                        }}
                      />
                    </Form.Item>
                  )
                );
              }}
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              dependencies={[VehicleKey.RENTED_FROM, VehicleKey.IS_LEASED]}
            >
              {({getFieldValue}) => {
                return (
                  getFieldValue(VehicleKey.IS_LEASED) && (
                    <Form.Item
                      name={VehicleKey.RENTED_TO}
                      label={VehicleLabel.RENTED_TO}
                      shouldUpdate={true}
                      rules={[
                        {
                          required: true,
                          message: CommonErrors.mandatory(
                            VehicleLabel.RENTED_TO
                          )
                        }
                      ]}
                      validateFirst={true}
                      hasFeedback
                      labelCol={{span: 24}}
                    >
                      <MomentDatePicker
                        disabledDate={(current) => {
                          return (
                            getFieldValue(VehicleKey.RENTED_FROM) > current
                          );
                        }}
                        inputReadOnly
                        format="DD-MM-YYYY"
                        style={{width: '100%'}}
                        showNow={false}
                        disabled={!getFieldValue(VehicleKey.RENTED_FROM)}
                        placeholder={`Choose ${VehicleLabel.RENTED_TO}`}
                      />
                    </Form.Item>
                  )
                );
              }}
            </Form.Item>
          </Col>
        </>
      )}
      {(currentStep === 2 || defaultDisplayStep) && (
        <>
          {!defaultDisplayStep && (
            <Col className="gutter-row">
              <Form.Item
                name={documentUrlKeyLabel.key}
                label={documentUrlKeyLabel.label}
                labelCol={{span: 24}}
              >
                <AttachedDocumentUpload
                  uploadEndpoint={
                    API_ENDPOINT_JSON.vehicleUploadAttachedDocument
                  }
                />
              </Form.Item>
            </Col>
          )}
          <MonthlyYearlyDrawerForm
            config={monYear}
            data={getFieldValue(TF) as MYData[]}
          />
        </>
      )}
    </>
  );
};

export default VehicleForm;
